/**
 * Builds "look this player up" search links for Perfect Game and Prep Baseball Report (PBR).
 *
 * Most 14U+ players have a PG/PBR profile, but most younger ones don't, and we don't want to curate
 * links by hand. So instead of deep-linking we open a site-scoped Google search. It degrades
 * gracefully (no profile => "no results" instead of a 404) and lets the user disambiguate common
 * names themselves. We scope by the team's state (PBR profile URLs are state-keyed) but deliberately
 * leave grad year out: it can only be guessed from the age group + season year, and that guess is
 * wrong often enough (play-up teams, season year reflecting when the team was created) that a bad
 * year would steer the search away from the real profile.
 */
export interface ProfileSearchContext {
  /** Display name. A trailing ", #12" jersey suffix is stripped before searching. */
  name: string;
  state?: string | null;
}

export interface ProfileSearchUrls {
  perfectGame: string;
  pbr: string;
}

const googleSiteSearch = (terms: Array<string | null | undefined>, site: string): string => {
  const query = [...terms, `site:${site}`].filter(Boolean).join(" ");
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
};

/** Strips a trailing ", #12" jersey suffix so only the player's name is searched. */
export const nameForSearch = (name: string): string => name.replace(/,\s*#.*$/, "").trim();

export const buildProfileSearchUrls = ({ name, state }: ProfileSearchContext): ProfileSearchUrls => {
  const terms = [nameForSearch(name), state ?? undefined];
  return {
    perfectGame: googleSiteSearch(terms, "perfectgame.org/Players"),
    pbr: googleSiteSearch(terms, "prepbaseballreport.com/profiles"),
  };
};

/**
 * Whether to surface PG/PBR profile links for a team's age group. These recruiting profiles only
 * exist for ~14U and older (high-school age), so we hide them for 13U and below.
 */
export const isRecruitingAgeGroup = (ageGroup: string | null | undefined): boolean => {
  const group = (ageGroup ?? "").trim();
  if (!group) return false;

  // Discrete "NNU"/"NNO" (e.g. 14U, 18O): 14 and up.
  const discrete = /^(\d{1,2})[UO]$/i.exec(group);
  if (discrete) return Number(discrete[1]) >= 14;

  // School levels: high school / college are 14+, middle / elementary are not.
  if (/^high_/i.test(group) || group.toLowerCase() === "college") return true;
  if (/^middle_/i.test(group) || group.toLowerCase() === "elementary") return false;

  // Coarse ranges: only "Under 13" is clearly below 14U.
  if (/under\s*13/i.test(group)) return false;
  return true;
};
